import React, { useState, useEffect } from 'react';
import { getAvatarUrlAsync } from 'utils/avatarUtils';

interface AvatarProps {
  uid: string;
  displayName: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({ uid, displayName, size = 'medium', className = '' }) => {
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Size classes
  const sizeClasses = {
    small: 'h-8 w-8 text-sm',
    medium: 'h-12 w-12 text-base',
    large: 'h-20 w-20 text-xl'
  };

  useEffect(() => {
    let isMounted = true;

    const loadAvatar = async () => {
      try {
        const url = await getAvatarUrlAsync(uid);
        
        if (isMounted) {
          if (url) {
            setAvatarUrl(url);
            setError(false);
          } else {
            setError(true);
          }
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadAvatar();

    return () => {
      isMounted = false;
    };
  }, [uid, displayName]);

  const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (loading) {
    // Loading skeleton
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`} />
    );
  }

  if (error || !avatarUrl) {
    // Fallback to initials
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-brand-500 flex items-center justify-center text-white font-bold ${className}`}>
        {getInitials(displayName)}
      </div>
    );
  }

  // Show avatar image
  return (
    <img
      src={avatarUrl}
      alt={displayName}
      className={`${sizeClasses[size]} rounded-full object-cover ${className}`}
      onError={() => setError(true)}
    />
  );
};

export default Avatar;